// Blog Management Utilities using backend API
const API_URL = process.env.NODE_ENV === 'development' 
  ? 'http://localhost:4000/api/blogposts' 
  : '/api/blogposts';

// Get blog posts from backend
export const getBlogPosts = async () => {
  try {
    const response = await fetch(API_URL);
    if (!response.ok) throw new Error('Failed to fetch blog posts');
    const data = await response.json();
    return data.posts || [];
  } catch (error) {
    console.error('Error loading blog posts:', error);
    return [];
  }
};

// Save blog posts to localStorage (as a fallback)
export const saveBlogPostsToLocalStorage = (posts) => {
  try {
    localStorage.setItem('blogPosts', JSON.stringify({ posts }));
    return true;
  } catch (error) {
    console.error('Error saving to localStorage:', error);
    return false;
  }
};

// Load blog posts from localStorage
export const loadBlogPostsFromLocalStorage = () => {
  try {
    const data = localStorage.getItem('blogPosts');
    return data ? JSON.parse(data).posts : null;
  } catch (error) {
    console.error('Error loading from localStorage:', error);
    return null;
  }
};

// Add new blog post via backend
export const addBlogPost = async (newPost) => {
  try {
    const response = await fetch(API_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(newPost)
    });
    if (!response.ok) throw new Error('Failed to add blog post');
    const post = await response.json();
    return { success: true, post };
  } catch (error) {
    console.error('Error adding blog post:', error);
    return { success: false, error: error.message };
  }
};

// Delete blog post via backend
export const deleteBlogPost = async (postId) => {
  try {
    const response = await fetch(`${API_URL}?id=${postId}`, {
      method: 'DELETE'
    });
    if (!response.ok) throw new Error('Failed to delete blog post');
    return { success: true };
  } catch (error) {
    console.error('Error deleting blog post:', error);
    return { success: false, error: error.message };
  }
};

// Update blog post
export const updateBlogPost = (postId, updates) => {
  try {
    // Get current posts
    let currentPosts = loadBlogPostsFromLocalStorage();
    if (!currentPosts) {
      currentPosts = getBlogPosts();
    }

    // Find and update the post
    const updatedPosts = currentPosts.map(post => 
      post.id === postId ? { ...post, ...updates } : post
    );

    // Save to localStorage
    saveBlogPostsToLocalStorage(updatedPosts);

    return { success: true, posts: updatedPosts };
  } catch (error) {
    console.error('Error updating blog post:', error);
    return { success: false, error: error.message };
  }
};

// Export blog posts as JSON file (download)
export const exportBlogPosts = () => {
  try {
    const posts = loadBlogPostsFromLocalStorage() || getBlogPosts();
    const dataStr = JSON.stringify({ posts }, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `blogPosts-${new Date().toISOString().split('T')[0]}.json`;
    link.click();
    URL.revokeObjectURL(url);
    return true;
  } catch (error) {
    console.error('Error exporting blog posts:', error);
    return false;
  }
};

// Import blog posts from JSON file
export const importBlogPosts = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = JSON.parse(e.target.result);
        if (data.posts && Array.isArray(data.posts)) {
          saveBlogPostsToLocalStorage(data.posts);
          resolve({ success: true, posts: data.posts });
        } else {
          reject(new Error('Invalid file format'));
        }
      } catch (error) {
        reject(error);
      }
    };
    reader.onerror = () => reject(new Error('Error reading file'));
    reader.readAsText(file);
  });
}; 